import api from 'services/api';
import { getSummary, refreshDashboard, GET_SUMMARY_SUCCEED_DASHBOARD } from 'Screens/Contextualization/Dashboard/actions/Actions';
import { newGetVulns } from 'store/Manage/filterActions';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { getFirstScanFileAmount, selectCommands, selectUnfinishedCommands } from 'store/FileUploaderContext/selector';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import moment from 'moment';
import { getHostsCountInWs, getVulnsCountInWs } from 'Screens/Contextualization/Faraday/actions/Actions';
import { hasFinishedProcessingFirstScanCommand, isProcessingAFirstScan } from '../functions';
import { getData as getHostData } from 'store/Host/actions';
import { getHostsData as getHostsGeneralData } from 'store/HostsGeneral/actions';
import { getVulnsGeneral } from 'store/ManageGeneral/actions';
import { selectExploreGroupPayload } from 'store/GroupVulns/selectors';
import { closeExploreGroup } from 'store/GroupVulns/actions';
import { redirect } from 'store/Router/actions';
import {
  extractCommandIdentifier,
  isCommandError,
  isCommandInProgress,
  normalizeCommandIdentifier
} from '../groupingPolling/utils';

export const ADD_COMMAND = 'ADD_COMMAND';
export const COMMAND_ERROR = 'COMMAND_ERROR';
export const UPDATE_COMMAND_LIST_STATUS = 'UPDATE_COMMAND_LIST_STATUS';
export const UPDATE_FIRST_SCAN_FILE_AMOUNT = 'UPDATE_FIRST_SCAN_FILE_AMOUNT';
export const UPDATE_FIRST_SCAN_FILE_PERCENTAGE = 'UPDATE_FIRST_SCAN_FILE_PERCENTAGE';

export const SHOW_PROCESSING_QUEUE = 'SHOW_PROCESSING_QUEUE';
export const REMOVE_COMMAND = 'REMOVE_COMMAND';
export const RESET_FILE_UPLOAD_CONTEXT = 'RESET_FILE_UPLOAD_CONTEXT';
export const EXPAND_PROCESSING_QUEUE = 'EXPAND_PROCESSING_QUEUE';
export const SET_ERROR_IN_COMMAND = 'SET_ERROR_IN_COMMAND';
export const REMOVE_UNFINISHED_COMMANDS = 'REMOVE_UNFINISHED_COMMANDS';
export const MARK_COMMAND_FINISHED = 'MARK_COMMAND_FINISHED';
export const PATCH_COMMAND = 'PATCH_COMMAND';

const exploreRoutePattern = /^\/vulns\/([^/]+)\/groups\/([^/]+)$/;

const getCurrentPathname = () => window.location.pathname || '';

const normalizeGroupIdentifier = (value) => {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const buildExploreGroupFilter = ({ groupId, groupHeaderId }) => {
  if (groupId !== null && typeof groupId !== 'undefined') {
    return {
      name: 'group_id',
      op: 'eq',
      val: groupId
    };
  }

  if (groupHeaderId !== null && typeof groupHeaderId !== 'undefined') {
    return {
      name: 'id',
      op: 'eq',
      val: groupHeaderId
    };
  }

  return null;
};

const ensureExploreGroupStillValid = async (dispatch, getState, pathname) => {
  const match = (pathname || '').match(exploreRoutePattern);
  if (!match) return;

  const [, ws, routeGroupIdRaw] = match;
  if (!ws || !routeGroupIdRaw || routeGroupIdRaw === 'new') return;

  const state = getState();
  const explorePayload = selectExploreGroupPayload(state);
  const payloadGroupId = get(explorePayload, 'groupId', null);
  const payloadGroupHeaderId = get(explorePayload, 'groupHeaderId', null);

  const resolvedGroupIdRaw = payloadGroupId !== null && String(payloadGroupId) === String(routeGroupIdRaw)
    ? payloadGroupId
    : routeGroupIdRaw;
  const resolvedGroupId = normalizeGroupIdentifier(resolvedGroupIdRaw);
  const resolvedGroupHeaderId = normalizeGroupIdentifier(payloadGroupHeaderId);

  if (resolvedGroupId === null || typeof resolvedGroupId === 'undefined') return;

  const validationFilter = buildExploreGroupFilter({
    groupId: resolvedGroupId,
    groupHeaderId: resolvedGroupHeaderId
  });
  if (!validationFilter) return;

  try {
    const response = await api.manage.newGetVulns(ws, { filters: [validationFilter] });
    const vulnerabilities = get(response, 'vulnerabilities', []);
    const groupSize = Array.isArray(vulnerabilities) ? vulnerabilities.length : 0;

    if (groupSize <= 1) {
      dispatch(closeExploreGroup());
      dispatch(redirect(`/manage/${ws}`));
    }
  } catch (error) {
    // ignore validation failures; the next refresh attempt will retry.
  }
};

const refreshCurrentContext = async (dispatch, getState, pathname) => {
  if (pathname.includes('/manage') || pathname.startsWith('/vulns/')) {
    await dispatch(newGetVulns());
    if (pathname.startsWith('/vulns/')) {
      await ensureExploreGroupStillValid(dispatch, getState, pathname);
    }
    if (pathname.includes('/manage')) {
      await dispatch(getVulnsCountInWs());
      await dispatch(getHostsCountInWs());
    }
    return;
  }

  if (pathname.includes('/feed')) {
    await dispatch(refreshDashboard());
    await dispatch(getSummary());
    return;
  }

  if (pathname.includes('/host/')) {
    await dispatch(getHostData());
    return;
  }

  if (pathname.includes('/hosts')) {
    await dispatch(getHostsGeneralData());
    return;
  }

  if (pathname.includes('/vulnerabilities')) {
    await dispatch(getVulnsGeneral());
  }
};

const hasTimedOut = (command, commandStatus) => {
  const startReference =
    command?.queuedAt ??
    commandStatus?.metadata?.create_time ??
    commandStatus?.create_time;

  if (!startReference) return false;
  const difference = moment().diff(startReference, 'minutes');
  if (!Number.isFinite(difference) || difference < 0) return false;
  return difference >= 150;
};

let isRetrying = false;

function dispatchReloads (wsData) {
  return (dispatch) => {
    if (window.location.href.includes('/manage')) {
      dispatch(newGetVulns());
      dispatch(getVulnsCountInWs());
      dispatch(getHostsCountInWs());
    } else if (window.location.href.includes('/feed')) {
      dispatch(refreshDashboard());
      if (wsData) {
        dispatch({ type: GET_SUMMARY_SUCCEED_DASHBOARD, data: wsData });
      } else {
        dispatch(getSummary());
      }
    } else if (window.location.href.includes('/host/')) {
      dispatch(getHostData());
    } else if (window.location.href.includes('/hosts')) {
      dispatch(getHostsGeneralData());
    } else if (window.location.href.includes('/vulnerabilities')) {
      dispatch(getVulnsGeneral());
    }
  };
}

function retryUntilWsHasData (maxRetries = 20) {
  return (dispatch, getState) => {
    if (isRetrying) return;
    isRetrying = true;
    const workspace = selectCurrentWorkspace(getState());
    if (!workspace) {
      dispatch(dispatchReloads());
      isRetrying = false;
      return;
    }
    let retries = 0;
    const attempt = async () => {
      try {
        const wsData = await api.workspace.getWsStats(workspace);
        const hasData = wsData && wsData.stats && wsData.stats.total_vulns > 0;
        if (hasData || retries >= maxRetries) {
          dispatch(dispatchReloads(wsData));
          isRetrying = false;
          return;
        }
      } catch (e) {
        dispatch(dispatchReloads());
        isRetrying = false;
        return;
      }
      retries += 1;
      setTimeout(attempt, 3000);
    };
    setTimeout(attempt, 3000);
  };
}

export function updateFirstScanFileAmount (value) {
  return (dispatch) => {
    dispatch({ type: UPDATE_FIRST_SCAN_FILE_AMOUNT, value });
  };
}

export function updateFirstScanFilePercentage (value) {
  return (dispatch) => {
    dispatch({ type: UPDATE_FIRST_SCAN_FILE_PERCENTAGE, value });
  };
}

export function updateCommandsStatus () {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const commands = selectCommands(state);
      const unfinishedCommands = selectUnfinishedCommands(state);
      const firstScanFileAmount = getFirstScanFileAmount(state);
      const unfinishedNonGroupingCommands = unfinishedCommands.filter((command) => !command?.isGrouping);
      const uploadedUnfinishedCommands = unfinishedNonGroupingCommands.filter((command) => command.command_id);

      const promises = uploadedUnfinishedCommands.map((command) => api.workspace.getReportStatus(command.command_id));
      const results = await Promise.all(promises);
      const resultsById = new Map(results.map((result) => [
        normalizeCommandIdentifier(extractCommandIdentifier(result)),
        result
      ]));

      const commandListUpdated = commands.map((command) => {
        if (command.finished || command?.isGrouping || !command.command_id) {
          return {
            ...command,
            queuedAt: command.queuedAt ?? Date.now()
          };
        }

        const commandStatus = resultsById.get(normalizeCommandIdentifier(command.command_id));
        if (!commandStatus) {
          return {
            ...command,
            queuedAt: command.queuedAt ?? Date.now()
          };
        }

        const timeOut = hasTimedOut(command, commandStatus);
        const finished = !isCommandInProgress(commandStatus.duration, commandStatus.status) || timeOut;
        const error = isCommandError(commandStatus.duration, commandStatus.status) || timeOut;
        const warning = commandStatus.warnings;

        return {
          ...command,
          finished,
          error,
          timeOut,
          warning,
          queuedAt: command.queuedAt ?? Date.now()
        };
      });

      const updatedFirstScanCommands = commandListUpdated.filter((c) => c.fileType === 'firstScan');
      const updatedFSAmount = updatedFirstScanCommands.length;

      if (firstScanFileAmount < updatedFSAmount) {
        dispatch(updateFirstScanFileAmount(updatedFSAmount));
      }

      if (isProcessingAFirstScan(commandListUpdated)) {
        const finishedFirstScanAmount = firstScanFileAmount - updatedFSAmount;
        const percentage = (finishedFirstScanAmount * 100) / firstScanFileAmount;
        const finalValue = percentage || 0;
        dispatch(updateFirstScanFilePercentage(finalValue));
      }

      if (isEmpty(promises) && isEmpty(unfinishedNonGroupingCommands)) {
        await refreshCurrentContext(dispatch, getState, getCurrentPathname());
        dispatch(retryUntilWsHasData());
        dispatch(updateFirstScanFileAmount(0));
      }

      if (!isRetrying && getCurrentPathname().includes('/manage') && hasFinishedProcessingFirstScanCommand(commandListUpdated)) {
        dispatch(newGetVulns());
      }

      return dispatch({ type: UPDATE_COMMAND_LIST_STATUS, data: commandListUpdated });
    } catch (error) {
      return dispatch({ type: COMMAND_ERROR, error });
    }
  };
}

export function showProcessingQueue (show) {
  return (dispatch) => {
    dispatch({ type: SHOW_PROCESSING_QUEUE, show });
  };
}

export function expandProcessingQueue (expand) {
  return (dispatch) => {
    dispatch({ type: EXPAND_PROCESSING_QUEUE, expand });
  };
}

export function removeCommand (commandId, mockCommandId) {
  return (dispatch) => {
    dispatch({ type: REMOVE_COMMAND, commandId, mockCommandId });
  };
}

export function resetUploadContextMenu () {
  return (dispatch) => {
    dispatch({ type: RESET_FILE_UPLOAD_CONTEXT });
  };
}

export function refreshCurrentRouteContext () {
  return async (dispatch, getState) => {
    await refreshCurrentContext(dispatch, getState, getCurrentPathname());
  };
}

export function removeUnfinishedCommands () {
  return (dispatch) => {
    dispatch({ type: REMOVE_UNFINISHED_COMMANDS });
  };
}

export const enqueueCommand = (command) => (dispatch) => {
  dispatch({ type: ADD_COMMAND, command: { ...command, queuedAt: command.queuedAt ?? Date.now() } });
  dispatch(showProcessingQueue(true));
  dispatch(expandProcessingQueue(true));
};

export function setErrorInCommand (commandId, mockCommandId, name, errorMessage) {
  return (dispatch) => {
    dispatch({
      type: SET_ERROR_IN_COMMAND,
      commandId,
      mockCommandId,
      name,
      errorMessage
    });
  };
}

export const markCommandFinished = (mockCommandId, patch = {}) => (dispatch) => {
  dispatch({ type: MARK_COMMAND_FINISHED, mockCommandId, patch });
};

export const patchCommand = (target, patch = {}) => (dispatch) => {
  dispatch({ type: PATCH_COMMAND, target, patch });
};
